import { Link, useForm } from '@inertiajs/react'
import AuthLayout from '@/layouts/AuthLayout'
import { AuthCard } from '@/components/AuthCard'
import { FlashBanner } from '@/components/FlashBanner'
import { FormField } from '@/components/FormField'
import { ErrorBanner } from '@/components/ErrorBanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { ResultError } from '@/types/form'

interface Props {
  email?: string
  error?: ResultError
  registration_closed?: boolean
}

export default function Signup ({ email = '', error, registration_closed: registrationClosed = false }: Props): React.ReactElement {
  const { data, setData, post, processing } = useForm({ email, login: '' })

  function handleSubmit (e: React.SubmitEvent): void {
    e.preventDefault()
    post('/auth/registration')
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className='flex items-center gap-2.5 mb-7'>
          <div className='w-9 h-9 bg-(--bp-accent) rounded-lg flex items-center justify-center text-[13px] font-extrabold text-white tracking-tighter'>
            BP
          </div>
          <div className='text-[18px] font-bold tracking-[-0.04em] text-(--bp-accent)'>Blueprint</div>
        </div>

        {registrationClosed
          ? (
            <>
              <div className='text-[22px] font-bold tracking-[-0.04em] text-(--bp-ink) mb-1.5'>アカウントの発行について</div>
              <div className='text-[13px] text-(--bp-ink3) mb-7 leading-[1.55]'>
                現在、新規登録は受け付けておりません。ゲストアカウントの発行をご希望の場合は、お問い合わせください。
              </div>

              <Button asChild size='lg'>
                <a href='/contact'>お問い合わせはこちら</a>
              </Button>

              <div className='mt-5 text-center text-xs text-(--bp-ink3)'>
                すでにアカウントをお持ちの方は{' '}
                <Link href='/auth/session/new' className='text-(--bp-accent) font-medium no-underline'>ログイン</Link> へ
              </div>
            </>
            )
          : (
            <>
              <div className='text-[22px] font-bold tracking-[-0.04em] text-(--bp-ink) mb-1.5'>アカウントを作成</div>
              <div className='text-[13px] text-(--bp-ink3) mb-7 leading-[1.55]'>メールアドレスに登録リンクを送信します</div>

              <FlashBanner />

              {(error != null) && <ErrorBanner message={error.error_message} />}

              <form onSubmit={handleSubmit}>
                <div className='hidden' aria-hidden='true'>
                  <label>
                    ログイン
                    <Input
                      value={data.login}
                      onChange={(e) => setData('login', e.target.value)}
                      tabIndex={-1}
                      autoComplete='off'
                    />
                  </label>
                </div>
                <FormField label='メールアドレス'>
                  <Input
                    type='email'
                    value={data.email}
                    onChange={(e) => setData('email', e.target.value)}
                    placeholder='your@email.com'
                    autoComplete='email'
                    required
                  />
                </FormField>
                <Button size='lg' type='submit' disabled={processing}>
                  登録リンクを送る
                </Button>
              </form>

              <div className='text-[11px] text-(--bp-ink3) mt-4 leading-[1.6] text-center'>
                パスワードは不要です。メールに届くリンクをクリックするだけで登録・ログインできます。
              </div>

              <div className='mt-5 text-center text-xs text-(--bp-ink3)'>
                すでにアカウントをお持ちの方は{' '}
                <Link href='/auth/session/new' className='text-(--bp-accent) font-medium no-underline'>ログイン</Link> へ
              </div>
            </>
            )}
      </AuthCard>
    </AuthLayout>
  )
}
